import React from 'react';
import { AtlasMapBlock, AtlasTool } from '../../types';
import { useAtlas } from '../../context/AtlasContext';
import { TrashIcon } from '@phosphor-icons/react';

interface MappingBlockProps {
    block: AtlasMapBlock;
    isSelected: boolean;
    onSelect: () => void;
    scale: number;
    tool: AtlasTool;
}

export const MappingBlock: React.FC<MappingBlockProps> = ({
    block,
    isSelected,
    onSelect,
    scale,
    tool
}) => {
    const { deleteMapBlock } = useAtlas();
    const [showDelete, setShowDelete] = React.useState(false);

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (confirm('Delete this map block?')) {
            await deleteMapBlock(block.id);
        }
    };

    // Cursor based on tool
    const getCursor = () => {
        if (tool === 'eraser') return 'cursor-pointer';
        if (tool === 'select') return 'cursor-pointer';
        return 'cursor-default';
    };

    return (
        <div
            data-block="mapping"
            className={`absolute group z-0 ${getCursor()}`}
            style={{
                left: block.canvas_x,
                top: block.canvas_y,
                width: block.width,
                height: block.height
            }}
            onClick={(e) => {
                e.stopPropagation();
                onSelect();
            }}
            onMouseEnter={() => setShowDelete(true)}
            onMouseLeave={() => setShowDelete(false)}
        >
            {/* Selection Ring / Eraser Highlight */}
            <div
                className={`absolute inset-0 rounded-lg transition-all pointer-events-none ${
                    tool === 'eraser'
                        ? 'ring-2 ring-red-500/50 group-hover:ring-red-500 group-hover:bg-red-500/20'
                        : isSelected
                            ? 'ring-2 ring-indigo-500/50 ring-offset-1 ring-offset-zinc-950'
                            : ''
                }`}
            />

            {/* Image - Map blocks sit UNDER the grid visually */}
            <div className="w-full h-full rounded-lg overflow-hidden opacity-90 group-hover:opacity-100 transition-opacity">
                <img
                    src={block.image_url}
                    alt="Map terrain"
                    className="w-full h-full object-cover"
                    draggable={false}
                />
            </div>

            {/* Delete Button (on hover when selected) */}
            {isSelected && showDelete && tool === 'select' && (
                <button
                    onClick={handleDelete}
                    className="absolute -top-3 -right-3 p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-colors z-10"
                    title="Delete block"
                >
                    <TrashIcon className="w-3.5 h-3.5" />
                </button>
            )}

            {/* Subtle border to blend with grid */}
            <div className="absolute inset-0 rounded-lg border border-zinc-800/50 pointer-events-none" />
        </div>
    );
};
